import React from 'react';
import { Input } from 'reactstrap';
import DatePickerInput from '../../../../components/DatePickerInput';
import { SidebarBackdrop, RightSidebarPanel, SidebarHeader, SidebarBody } from './styles';

export interface FiltrosState {
  busca: string;
  prioridade: string[];
  situacao: string[];
  membros_ids: string[];
  tags: string;
  dataPrazoInicial: string;
  dataPrazoFinal: string;
  prazoQuick: string[]; // ['atraso', 'hoje', 'semana', 'sem_prazo']
  semMembros: boolean;
  atribuidosAMim: boolean;
}

interface TrelloFiltersProps {
  open: boolean;
  onClose: () => void;
  filtros: FiltrosState;
  onChange: (campo: keyof FiltrosState, valor: any) => void;
  colaboradores: any[];
  currentUserId?: number;
}

const PRIORIDADES = [
  { value: 'baixa',   label: 'Baixa',   color: '#4299e1' },
  { value: 'normal',  label: 'Normal',  color: '#2dce89' },
  { value: 'alta',    label: 'Alta',    color: '#fb6340' },
  { value: 'urgente', label: 'Urgente', color: '#f5365c' },
];

const SITUACOES = [
  { value: 'aberto',    label: 'Aberto',    color: '#5e72e4', icon: 'fas fa-folder-open' },
  { value: 'concluido', label: 'Concluído', color: '#2dce89', icon: 'fas fa-check-circle' },
  { value: 'cancelado', label: 'Cancelado', color: '#f5365c', icon: 'fas fa-times-circle' },
  { value: 'arquivado', label: 'Arquivado', color: '#8898aa', icon: 'fas fa-archive' },
];

const PRAZOS_QUICK = [
  { value: 'sem_prazo', label: 'Sem datas',                 color: '#8898aa', icon: 'far fa-calendar-times' },
  { value: 'atraso',    label: 'Em atraso',                 color: '#f5365c', icon: 'fas fa-clock' },
  { value: 'hoje',      label: 'Entregue hoje',             color: '#fb6340', icon: 'far fa-clock' },
  { value: 'dia',       label: 'A ser entregue em um dia',  color: '#f6c23e', icon: 'far fa-clock' },
  { value: 'semana',    label: 'A ser entregue em 7 dias',  color: '#5e72e4', icon: 'far fa-calendar-alt' },
  { value: 'mes',       label: 'A ser entregue em 30 dias', color: '#11cdef', icon: 'far fa-calendar-alt' },
];

function toggle(arr: any[], val: any): any[] {
  return arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
}

function Avatar({ user, size = 28 }: { user: any; size?: number }) {
  const [imgError, setImgError] = React.useState(false);
  const nome = user?.name || user?.nome || '?';
  const iniciais = nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s: string) => s[0])
    .join('')
    .toUpperCase() || '?';
  const colors = ['#5e72e4', '#11cdef', '#2dce89', '#fb6340', '#f5365c', '#8965e0', '#f3a4b5'];
  const colorIdx = (Number(user?.id || user?.user_id) || 0) % colors.length;
  const avatarUrl = user?.avatar_url || user?.avatar;

  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={nome}
        onError={() => setImgError(true)}
        style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div
      title={nome}
      style={{
        width: size, height: size, borderRadius: '50%',
        background: colors[colorIdx],
        color: '#fff', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
        lineHeight: 1,
      }}
    >
      {iniciais}
    </div>
  );
}

interface CheckRowProps {
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}
function CheckRow({ checked, onToggle, children }: CheckRowProps) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
        userSelect: 'none', fontSize: '0.85rem',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <div
        style={{
          width: 16, height: 16, borderRadius: 3,
          border: `2px solid ${checked ? '#5e72e4' : '#cbd5e1'}`,
          background: checked ? '#5e72e4' : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {checked && <i className="fas fa-check" style={{ fontSize: 9, color: '#fff' }} />}
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}
function Section({ title, children }: SectionProps) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div
        style={{
          fontSize: '0.72rem', fontWeight: 700,
          color: '#64748b', textTransform: 'uppercase',
          letterSpacing: '0.04em', marginBottom: 8,
          padding: '0 8px',
        }}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

const TrelloFilters: React.FC<TrelloFiltersProps> = ({
  open, onClose, filtros, onChange, colaboradores, currentUserId,
}) => {
  const totalAtivos = contagemFiltros(filtros);

  return (
    <>
      <SidebarBackdrop open={open} onClick={onClose} style={{ zIndex: 1049 }} />
      <RightSidebarPanel open={open} style={{ width: 380, maxWidth: '92vw' }}>
        <SidebarHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <h3 className="mb-0">Filtro</h3>
            {totalAtivos > 0 && (
              <span
                style={{
                  background: '#5e72e4', color: '#fff',
                  fontSize: '0.7rem', fontWeight: 600,
                  padding: '2px 8px', borderRadius: 10,
                }}
              >
                {totalAtivos}
              </span>
            )}
          </div>
          <i
            className="fas fa-times"
            onClick={onClose}
            style={{ cursor: 'pointer', fontSize: '1.1rem', color: '#8898aa' }}
          />
        </SidebarHeader>
        <SidebarBody style={{ padding: 16 }}>
          {/* Palavra-chave */}
          <Section title="Palavra-chave">
            <Input
              type="text"
              placeholder="Insira uma palavra-chave..."
              bsSize="sm"
              value={filtros.busca}
              onChange={e => onChange('busca', e.target.value)}
              style={{ marginBottom: 6 }}
            />
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', padding: '0 2px' }}>
              Pesquise cartões pelo título.
            </div>
          </Section>

          {/* Tags */}
          <Section title="Etiquetas">
            <Input
              type="text"
              placeholder="Filtrar por tag..."
              bsSize="sm"
              value={filtros.tags}
              onChange={e => onChange('tags', e.target.value)}
            />
          </Section>

          {/* Membros */}
          <Section title="Membros">
            <CheckRow
              checked={filtros.semMembros}
              onToggle={() => onChange('semMembros', !filtros.semMembros)}
            >
              <div
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: '#e2e8f0', color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className="fas fa-user-slash" style={{ fontSize: 11 }} />
              </div>
              <span>Sem membros</span>
            </CheckRow>
            {currentUserId && (
              <CheckRow
                checked={filtros.atribuidosAMim}
                onToggle={() => onChange('atribuidosAMim', !filtros.atribuidosAMim)}
              >
                <Avatar user={colaboradores.find((c: any) => Number(c.user_id || c.id) === Number(currentUserId)) || { id: currentUserId }} />
                <span>Cartões atribuídos a mim</span>
              </CheckRow>
            )}
            <div
              style={{
                marginTop: 6, paddingTop: 6,
                borderTop: '1px solid #f1f5f9',
                maxHeight: 200, overflowY: 'auto',
              }}
            >
              {colaboradores.map((c: any) => {
                const uid = String(c.user_id || c.id);
                const checked = filtros.membros_ids.includes(uid);
                return (
                  <CheckRow
                    key={uid}
                    checked={checked}
                    onToggle={() => onChange('membros_ids', toggle(filtros.membros_ids, uid))}
                  >
                    <Avatar user={c} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name || c.nome}
                    </span>
                  </CheckRow>
                );
              })}
            </div>
          </Section>

          {/* Prioridade */}
          <Section title="Prioridade">
            {PRIORIDADES.map(p => (
              <CheckRow
                key={p.value}
                checked={filtros.prioridade.includes(p.value)}
                onToggle={() => onChange('prioridade', toggle(filtros.prioridade, p.value))}
              >
                <div
                  style={{
                    width: 14, height: 14, borderRadius: 3,
                    background: p.color, flexShrink: 0,
                  }}
                />
                <span>{p.label}</span>
              </CheckRow>
            ))}
          </Section>

          {/* Card status */}
          <Section title="Situação do cartão">
            {SITUACOES.map(s => (
              <CheckRow
                key={s.value}
                checked={filtros.situacao.includes(s.value)}
                onToggle={() => onChange('situacao', toggle(filtros.situacao, s.value))}
              >
                <i className={s.icon} style={{ color: s.color, fontSize: 13, width: 16, textAlign: 'center' }} />
                <span>{s.label}</span>
              </CheckRow>
            ))}
          </Section>

          {/* Data de entrega */}
          <Section title="Data de entrega">
            {PRAZOS_QUICK.map(p => (
              <CheckRow
                key={p.value}
                checked={filtros.prazoQuick.includes(p.value)}
                onToggle={() => onChange('prazoQuick', toggle(filtros.prazoQuick, p.value))}
              >
                <div
                  style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: p.color, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className={p.icon} style={{ fontSize: 10 }} />
                </div>
                <span>{p.label}</span>
              </CheckRow>
            ))}
            <div
              style={{
                marginTop: 10, paddingTop: 10,
                borderTop: '1px solid #f1f5f9',
              }}
            >
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 6, padding: '0 8px' }}>
                Ou intervalo personalizado:
              </div>
              <div style={{ display: 'flex', gap: 6, padding: '0 8px' }}>
                <div style={{ flex: 1 }}>
                  <DatePickerInput
                    value={filtros.dataPrazoInicial || null}
                    onChange={(iso) => onChange('dataPrazoInicial', iso || '')}
                    placeholder="De"
                    showTime={false}
                    inputClassName=""
                    inputStyle={{
                      width: '100%',
                      fontSize: '0.78rem',
                      padding: '5px 9px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      color: '#525f7f',
                      outline: 'none',
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <DatePickerInput
                    value={filtros.dataPrazoFinal || null}
                    onChange={(iso) => onChange('dataPrazoFinal', iso || '')}
                    placeholder="Até"
                    showTime={false}
                    inputClassName=""
                    inputStyle={{
                      width: '100%',
                      fontSize: '0.78rem',
                      padding: '5px 9px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      color: '#525f7f',
                      outline: 'none',
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                    }}
                  />
                </div>
              </div>
            </div>
          </Section>

        </SidebarBody>
      </RightSidebarPanel>
    </>
  );
};

export function contagemFiltros(f: FiltrosState): number {
  let count = 0;
  if (f.busca) count++;
  if (f.tags) count++;
  if (f.prioridade.length) count += f.prioridade.length;
  if (f.situacao.length) count += f.situacao.length;
  if (f.membros_ids.length) count += f.membros_ids.length;
  if (f.prazoQuick.length) count += f.prazoQuick.length;
  if (f.semMembros) count++;
  if (f.atribuidosAMim) count++;
  if (f.dataPrazoInicial || f.dataPrazoFinal) count++;
  return count;
}

export default TrelloFilters;
